import React from 'react';
import Image from 'next/image';
import { CaseStudyMember } from '@/types/pages';
import { imageUrlBuilder } from '@/utils/helpers';
import gutterStyles from '@/styles/gutter.module.css';

interface Props {
  heading?: string;
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
  members?: CaseStudyMember[];
}

const CaseStudyTeam: React.FC<Props> = ({ heading, headingLevel = 'h2', members }) => {
  if (!members || members.length === 0) return null;

  const HeadingTag = headingLevel as keyof React.JSX.IntrinsicElements;

  return (
    <section>
      <div className={`mx-auto w-full max-w-a-wide ${gutterStyles.gutter}`}>
        <div className="max-w-5xl">
          {heading && (
            <div style={{ marginBottom: '1.25rem' }}>
              <HeadingTag className="mb-0 font-bold text-a-navy">{heading}</HeadingTag>
            </div>
          )}
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {members.map((member) => {
              const asset = member.image?.image?.asset;
              const photoUrl = asset
                ? imageUrlBuilder
                    .image(asset)
                    .width(160)
                    .height(160)
                    .fit('crop')
                    .crop('top')
                    .auto('format')
                    .quality(80)
                    .url()
                : null;

              return (
                <div key={member._key} className="flex items-center gap-3 min-w-0">
                  <div className="relative h-32 w-32 flex-shrink-0 overflow-hidden rounded-full border-2 border-a-sky bg-a-off-white">
                    {photoUrl && (
                      <Image
                        src={photoUrl}
                        alt={member.image?.alt || member.name}
                        fill
                        className="mb-0 rounded-full object-cover object-top"
                      />
                    )}
                  </div>
                  <div>
                    <p className="mb-0 text-lg font-semibold text-a-navy">{member.name}</p>
                    {(member.jobTitle || member.department?.department) && (
                      <p className="mb-0 mt-0 text-sm text-a-text-secondary">
                        {member.jobTitle || member.department?.department}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudyTeam;
